import {
  ArrowUpRight,
  Warehouse,
  Factory,
  Truck,
  Sprout,
  LineChart,
  Wallet,
  ReceiptText,
  Undo2,
  Coins,
  ChefHat,
  FileSpreadsheet,
  Boxes,
  Scale,
  Split,
  Calculator,
} from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';

// Кабинет оптовика, отдельное устанавливаемое приложение (PWA), как у поставщика.
// Локально http://localhost:5177, на проде задаётся через VITE_WHOLESALER_URL (поддомен).
const WHOLESALER_APP = import.meta.env.VITE_WHOLESALER_URL?.trim() || 'http://localhost:5177';

const AUDIENCE = [
  {
    icon: Factory,
    title: 'Производство',
    text: 'Пекарня, кондитерский цех, кулинария, цех полуфабрикатов. Готовите из сырья по рецепту и продаёте готовую продукцию.',
  },
  {
    icon: Truck,
    title: 'Опт и дистрибуция',
    text: 'Продаёте кафе, магазинам и на маркетплейсы. Нужен точный учёт закупок, остатков и выручки по каждой позиции.',
  },
  {
    icon: Sprout,
    title: 'Локальный производитель',
    text: 'Фермерское хозяйство, сыроварня, обжарка кофе. Небольшой ассортимент, но важна себестоимость и маржа.',
  },
];

const FEATURES = [
  {
    icon: LineChart,
    title: 'Аналитика',
    text: 'Выручка, себестоимость проданного, валовая и чистая прибыль, маржа. Топ позиций, способы оплаты, расходы по статьям.',
  },
  {
    icon: Wallet,
    title: 'Финансы',
    text: 'Кэшбук: журнал движения денег за любой период. Продажи в плюс, возвраты и расходы в минус, с разбивкой по наличным и карте.',
  },
  {
    icon: ReceiptText,
    title: 'Продажи с чеком',
    text: 'Продажа собирается в пару касаний: корзина, способ оплаты, печать чека. Склад и себестоимость пересчитываются сразу.',
  },
  {
    icon: Undo2,
    title: 'Возвраты',
    text: 'Возврат оформляется по конкретной продаже. Товар без рецепта возвращается на склад, готовая продукция корректирует только деньги.',
  },
  {
    icon: Coins,
    title: 'Расходы',
    text: 'Свои статьи затрат: аренда, зарплата, логистика, упаковка. Каждый расход попадает и в финансы, и в чистую прибыль.',
  },
  {
    icon: ChefHat,
    title: 'Тех-карты',
    text: 'Рецепт с брутто, потерями при обработке и нетто. Себестоимость и фудкост считаются сами и обновляются после каждой закупки.',
  },
  {
    icon: FileSpreadsheet,
    title: 'Приходные накладные',
    text: 'Документ создаётся черновиком, проверяется и проводится. После проведения позиции сами уходят на склад с новой ценой.',
  },
  {
    icon: Boxes,
    title: 'Склад и справочник',
    text: 'Товары и ингредиенты в одном справочнике с категориями. Остатки, минимальный запас и предупреждение, что пора докупить.',
  },
];

const AUTOMATION = [
  {
    icon: Scale,
    title: 'Себестоимость по средней взвешенной',
    text: 'Купили муку по 42 ₽, потом по 48 ₽. Система не заменяет цену, а считает средневзвешенную по фактическому остатку. Маржа не скачет от партии к партии.',
  },
  {
    icon: Split,
    title: 'Списание по брутто, а не по нетто',
    text: 'В рецепте 1 кг мяса и 18 % потерь при зачистке. Со склада уходит 1 кг, а не 820 г, и в себестоимость попадают отходы, как в жизни.',
  },
  {
    icon: Calculator,
    title: 'Пересчёт по цепочке',
    text: 'Подорожало сырьё, и себестоимость всей продукции, где оно используется, обновляется сама. Не нужно вручную править десятки карточек.',
  },
];

const STEPS = [
  {
    title: 'Справочник',
    text: 'Заводите категории, товары и ингредиенты с единицами измерения и минимальным запасом.',
  },
  {
    title: 'Тех-карты',
    text: 'Для готовой продукции описываете рецепт: брутто, потери, нетто. Себестоимость считается сама.',
  },
  {
    title: 'Приход',
    text: 'Оформляете накладную от поставщика и проводите её. Остатки и закупочные цены обновляются.',
  },
  {
    title: 'Продажи',
    text: 'Продаёте с чеком. Списание, выручка, прибыль и кэшбук заполняются без ручного ввода.',
  },
];

const FAQ = [
  {
    q: 'Чем это отличается от кассы Resstro для заведений?',
    a: 'Касса рассчитана на обслуживание гостей за столами: зал, официанты, смены. «Оптовик» про производство и продажу партиями: рецепты, приходные накладные, себестоимость и маржа по каждой позиции. Это отдельный кабинет со своим входом, он не связан с вашими заведениями.',
  },
  {
    q: 'Нужна ли касса или заведение в Resstro?',
    a: 'Нет. «Оптовик» работает сам по себе, достаточно регистрации по email. Если у вас уже есть заведение в Resstro, аккаунты остаются независимыми.',
  },
  {
    q: 'На чём работает кабинет?',
    a: 'В браузере на компьютере, планшете и телефоне. Ставить ничего не нужно, но кабинет можно добавить на рабочий стол как приложение. Чек печатается через диалог печати браузера.',
  },
  {
    q: 'Что происходит с данными после закупки?',
    a: 'При проведении накладной пересчитывается закупочная цена, а вслед за ней себестоимость всей продукции, в рецептах которой есть эта позиция. Прошлые продажи не переписываются: у них остаётся себестоимость на момент продажи.',
  },
  {
    q: 'Сколько сотрудников можно подключить?',
    a: 'В первой версии один вход на аккаунт, как в кабинете поставщика. Отдельные пользователи с ролями появятся позже.',
  },
];

const SUMMARY = [
  { label: 'Выручка', value: '1 284 500 ₽' },
  { label: 'Себестоимость', value: '712 300 ₽' },
  { label: 'Валовая прибыль', value: '572 200 ₽', accent: true },
  { label: 'Маржа', value: '44,5 %' },
];

const TOP_ITEMS = [
  { name: 'Хлеб бородинский, 0,5 кг', value: '186 400 ₽' },
  { name: 'Соус фирменный, 0,5 л', value: '142 900 ₽' },
  { name: 'Круассан с миндалём', value: '98 250 ₽' },
];

export default function Wholesalers() {
  return (
    <>
      <Header />

      <main>
        {/* Hero */}
        <section className="bg-paper section-divider">
          <div className="container-site py-16 sm:py-20 lg:py-24">
            <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.95fr]">
              <div className="max-w-xl">
                <span className="eyebrow">
                  <Warehouse size={13} strokeWidth={2.2} />
                  Resstro Оптовик
                </span>
                {/* Кегль ниже, чем .display: в заголовке есть «себестоимостью», при
                    5.75rem это слово не помещается в колонку и лезет на сводку. */}
                <h1 className="mt-6 text-balance text-[clamp(2.5rem,4.4vw,4.5rem)] font-medium leading-[1.03] tracking-tightest text-ink">
                  Производство и&nbsp;опт
                  <br />
                  <span className="text-ink/40">с&nbsp;честной себестоимостью</span>
                </h1>
                <p className="lead mt-7">
                  Отдельный кабинет для тех, кто производит и продаёт партиями. Рецепты, приходные
                  накладные, склад и продажи связаны между собой: вы видите не только выручку, но и
                  сколько на ней заработали.
                </p>

                <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <a href={`${WHOLESALER_APP}/signup`} className="btn-primary px-6 py-3.5">
                    Начать бесплатно
                    <ArrowUpRight size={16} strokeWidth={2} />
                  </a>
                  <a href={`${WHOLESALER_APP}/login`} className="btn-outline px-6 py-3.5">
                    Войти в кабинет
                  </a>
                </div>
                <p className="mt-5 text-[13px] text-ink/50">
                  3 дня бесплатно · затем 2 999 ₽/мес · без привязки карты
                </p>
              </div>

              {/* Превью сводки, статичная иллюстрация того, что считает кабинет.
                  Порядок DOM не переставляем: на узком экране заголовок должен идти
                  первым, иначе страница открывается с непонятных цифр. */}
              <div>
                <div className="border border-line bg-white">
                  <div className="flex items-center justify-between border-b border-line px-6 py-4">
                    <span className="text-[14px] font-medium text-ink">Сводка за месяц</span>
                    <span className="eyebrow">Аналитика</span>
                  </div>

                  <div className="grid grid-cols-2 gap-px bg-line">
                    {SUMMARY.map((m) => (
                      <div key={m.label} className="bg-white px-6 py-5">
                        <div className="text-[12px] text-ink/50">{m.label}</div>
                        <div
                          className={
                            m.accent
                              ? 'mt-1.5 text-[20px] font-medium tracking-tight text-green-500'
                              : 'mt-1.5 text-[20px] font-medium tracking-tight text-ink'
                          }
                        >
                          {m.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-line px-6 py-5">
                    <div className="eyebrow">Топ позиций</div>
                    <ul className="mt-4 space-y-3">
                      {TOP_ITEMS.map((item) => (
                        <li key={item.name} className="flex items-baseline justify-between gap-4">
                          <span className="text-[13px] text-ink/70">{item.name}</span>
                          <span className="shrink-0 text-[13px] font-medium text-ink">
                            {item.value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="mt-3 text-center text-[12px] text-ink/40 lg:text-right">
                  Пример данных кабинета
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Для кого */}
        <section className="section-divider">
          <div className="container-site py-20 sm:py-28">
            <span className="eyebrow">Для кого</span>
            <h2 className="h2 mt-5 max-w-2xl">Когда продаж мало, а расчётов много</h2>
            <p className="lead mt-6 max-w-2xl">
              Пять накладных в месяц можно вести и в таблице. Но как только в дело вступают рецепты,
              потери при обработке и разные закупочные цены, таблица начинает врать.
            </p>

            <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-3">
              {AUDIENCE.map((a) => (
                <div key={a.title} className="bg-white p-8">
                  <span className="grid h-11 w-11 place-items-center bg-paper text-ink">
                    <a.icon size={20} strokeWidth={1.8} />
                  </span>
                  <h3 className="h3 mt-6">{a.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink/65">{a.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Что внутри */}
        <section className="section-divider bg-paper">
          <div className="container-site py-20 sm:py-28">
            <span className="eyebrow">Что внутри</span>
            <h2 className="h2 mt-5 max-w-2xl">Восемь разделов, один цикл учёта</h2>

            <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2">
              {FEATURES.map((f) => (
                <div key={f.title} className="flex gap-5 bg-white p-8">
                  <span className="grid h-11 w-11 shrink-0 place-items-center bg-paper text-orange">
                    <f.icon size={20} strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="h3">{f.title}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-ink/65">{f.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Как это работает */}
        <section className="section-divider">
          <div className="container-site py-20 sm:py-28">
            <span className="eyebrow">Как это работает</span>
            <h2 className="h2 mt-5 max-w-2xl">Четыре шага, дальше всё само</h2>

            <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <div key={s.title} className="bg-white p-8">
                  <span className="text-[13px] font-medium text-ink/30">0{i + 1}</span>
                  <h3 className="h3 mt-5">{s.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink/65">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Что считает за вас */}
        <section className="section-divider bg-paper">
          <div className="container-site py-20 sm:py-28">
            <span className="eyebrow">Считает за вас</span>
            <h2 className="h2 mt-5 max-w-2xl">Три расчёта, из-за которых таблица врёт</h2>

            <div className="mt-14 space-y-px border border-line bg-line">
              {AUTOMATION.map((a) => (
                <div
                  key={a.title}
                  className="flex flex-col gap-5 bg-white p-8 sm:flex-row sm:items-start sm:gap-8 sm:p-10"
                >
                  <span className="grid h-11 w-11 shrink-0 place-items-center bg-paper text-orange">
                    <a.icon size={20} strokeWidth={1.8} />
                  </span>
                  <div className="sm:grid sm:flex-1 sm:grid-cols-[minmax(0,20rem)_1fr] sm:gap-8">
                    <h3 className="h3">{a.title}</h3>
                    <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-ink/65 sm:mt-0">
                      {a.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Частые вопросы */}
        <section className="section-divider">
          <div className="container-site py-20 sm:py-28">
            <span className="eyebrow">Частые вопросы</span>
            <h2 className="h2 mt-5 max-w-2xl">Коротко о главном</h2>

            <div className="mt-14 border-t border-line">
              {FAQ.map((item) => (
                <div
                  key={item.q}
                  className="grid gap-3 border-b border-line py-7 lg:grid-cols-[minmax(0,24rem)_1fr] lg:gap-10"
                >
                  <h3 className="text-[16px] font-medium leading-snug text-ink">{item.q}</h3>
                  <p className="max-w-2xl text-[14px] leading-relaxed text-ink/65">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Тариф */}
        <section className="section-divider bg-paper">
          <div className="container-site py-20 sm:py-28">
            <div className="mx-auto max-w-2xl border border-line bg-white p-10 text-center sm:p-14">
              <span className="eyebrow justify-center">Тариф</span>
              <div className="mt-7 flex items-end justify-center gap-2">
                <span className="text-[56px] font-medium leading-none tracking-tightest text-ink">
                  2 999 ₽
                </span>
                <span className="pb-2 text-ink/50">/ мес</span>
              </div>
              <p className="mt-4 text-[15px] text-ink/65">
                Первые <span className="font-medium text-ink">3 дня бесплатно</span>. Один тариф:
                все разделы, без лимитов на позиции, накладные и продажи.
              </p>
              <a href={`${WHOLESALER_APP}/signup`} className="btn-primary mt-9 px-7 py-3.5">
                Создать кабинет
                <ArrowUpRight size={16} strokeWidth={2} />
              </a>
              <p className="mt-5 text-[13px] text-ink/50">
                Уже есть кабинет?{' '}
                <a href={`${WHOLESALER_APP}/login`} className="font-medium text-orange">
                  Войти
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
